import type { ErrorReportContext } from "./error-report.type";
import type { ErrorLogData } from "../errors.service";

const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 4_000;
const MAX_URL_LENGTH = 1_000;
const MAX_CONTEXT_ENTRIES = 24;
const MAX_CONTEXT_STRING_LENGTH = 300;
const MAX_COMPONENT_STACK_LENGTH = 4_000;

const COMPONENT_STACK_KEY = "componentStack";

/**
 * Explicit allowlist of scalar diagnostic fields callers may attach to a
 * report. Anything not named here is dropped, regardless of how safe it
 * looks — this mirrors the backend `sanitizeLogContext` contract so both
 * sides agree on what a "diagnostic field" is.
 */
const CONTEXT_ALLOWED_KEYS = new Set([
  // Trusted-device login probe results (booleans and short labels only).
  "challengeState",
  "clientDomain",
  "cookiesEnabled",
  "cryptoKeyStorable",
  "ecdsaSupport",
  "errorMessage",
  "errorName",
  "inAppBrowser",
  "indexedDb",
  "localStorageAvailable",
  "privateModeSuspected",
  "protocol",
  "secureContext",
  "storagePersisted",
  "subtleCrypto",
  "timedOut",
  "timeoutPhase",
  "trustedDeviceFallback",
  "trustedDeviceStage",
  "app",
  "buildVersion",
  "clientId",
  "clientTimestamp",
  "clientType",
  "code",
  "component",
  "domain",
  "errorBoundary",
  "errorCategory",
  "errorComponent",
  "errorFile",
  "errorId",
  "errorLine",
  "errorSubcategory",
  "errorType",
  "isCommonError",
  "jobId",
  "language",
  "method",
  "navigationHistory",
  "operation",
  "referer",
  "requestId",
  "resourceId",
  "rootCause",
  "route",
  "routePath",
  "runtime",
  "screen",
  "source",
  "statusCode",
  "suggestedFix",
  "taskId",
  "timezone",
  "type",
  "uploadSessionId",
  "userId",
  "userType",
  "version",
  "viewport",
]);

/**
 * Purpose: strip bearer/basic/token/password/apiKey style secrets (both
 * `key: value` and quoted-JSON forms), full Cookie/Set-Cookie header lines,
 * credential-bearing URLs of any scheme, relative-URL query/hash segments,
 * and emails found inside free-form error text, then bound the length.
 * Applied to every string that may reach the outbound transport — message,
 * stack, and componentStack alike.
 *
 * @example
 * sanitizeErrorText(error.stack, 4000);
 */
export function sanitizeErrorText(
  value: string | undefined,
  maxLength: number = MAX_MESSAGE_LENGTH,
): string | undefined {
  if (!value) return undefined;

  const withoutAuthSchemes = value.replace(
    /\b(Bearer|Basic)\s+[^\s,;"']+/gi,
    "$1 [REDACTED]",
  );

  const withoutKeyedSecrets = withoutAuthSchemes.replace(
    /(^|[^a-z0-9_.-])(["']?)([a-z0-9_.-]*(?:password|passwd|token|authorization|cookie|api[-_]?key|secret|credentials|session[-_]?id)[a-z0-9_.-]*)\2\s*([:=])\s*("[^"]*"|'[^']*'|[^\s,;}&\]]+)/gi,
    "$1$2$3$2$4[REDACTED]",
  );

  const withoutUrlSecrets = withoutKeyedSecrets
    .replace(/\b(Set-Cookie|Cookie)\s*:\s*[^\r\n]*/gi, "$1: [REDACTED]")
    .replace(/\b[a-z][a-z0-9+.-]*:\/\/[^\s"'<>`]+/giu, sanitizeEmbeddedUrl)
    .replace(
      /(^|[\s(])((?:\/|\.\/|\.\.\/)[^\s"'<>`]*?)[?#][^\s"'<>`]*/gmu,
      "$1$2",
    );

  const withoutEmails = withoutUrlSecrets.replace(
    /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu,
    "[REDACTED_EMAIL]",
  );

  return withoutEmails.replace(/\beyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\b/gu, "[REDACTED_JWT]").slice(0, maxLength);
}

/**
 * Purpose: reduce a page/report URL to origin+pathname only, dropping the
 * query string, hash, and any userinfo (`user:pass@host`) segment.
 *
 * @example
 * sanitizeReportUrl("https://user:pw@app.test/account?token=abc#section");
 * // => "https://app.test/account"
 */
export function sanitizeReportUrl(rawUrl: string): string {
  try {
    const parsedUrl = new URL(rawUrl);

    return `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}`.slice(
      0,
      MAX_URL_LENGTH,
    );
  } catch {
    return "";
  }
}

/**
 * Purpose: restrict arbitrary caller-supplied context to an explicit
 * allowlist of scalar diagnostic fields, reading each field through its own
 * property descriptor so a hostile accessor property is never invoked.
 * `componentStack` is preserved separately with a larger bound so React
 * diagnostics survive while every other field stays short.
 *
 * @example
 * sanitizeErrorContext({ routePath: "/account", headers: { cookie: "x" } });
 * // => { routePath: "/account" }
 */
export function sanitizeErrorContext(
  context: Record<string, unknown> | undefined,
): ErrorReportContext | undefined {
  if (!isPlainRecord(context)) return undefined;

  const sanitizedEntries: ErrorReportContext = {};
  let entryCount = 0;

  const rawComponentStack = readOwnValue(context, COMPONENT_STACK_KEY);

  if (typeof rawComponentStack === "string") {
    const sanitizedComponentStack = sanitizeErrorText(
      rawComponentStack,
      MAX_COMPONENT_STACK_LENGTH,
    );

    if (sanitizedComponentStack) {
      sanitizedEntries[COMPONENT_STACK_KEY] = sanitizedComponentStack;
      entryCount += 1;
    }
  }

  for (const key of CONTEXT_ALLOWED_KEYS) {
    if (entryCount >= MAX_CONTEXT_ENTRIES) break;

    const sanitizedValue = sanitizeContextValue(readOwnValue(context, key));

    if (sanitizedValue === undefined) continue;

    sanitizedEntries[key] = sanitizedValue;
    entryCount += 1;
  }

  return entryCount > 0 ? sanitizedEntries : undefined;
}

/**
 * Purpose: build a stable, bounded signature used by the report guard to
 * dedupe repeated errors from the same source within the TTL window. The
 * server reporter passes the verified request tenant domain so identical
 * errors from different tenants never suppress one another.
 *
 * @example
 * buildErrorReportSignature("TypeError: x is undefined", stack, "window-error", "tenant-a.test");
 */
export function buildErrorReportSignature(
  message: string,
  stack: string | undefined,
  source: string,
  domain: string = "",
): string {
  const firstStackLine = stack?.split("\n")[1]?.trim() ?? "";

  return `${domain}:${source}:${message.slice(0, 160)}:${firstStackLine.slice(0, 120)}`;
}

function sanitizeContextValue(
  value: unknown,
): ErrorReportContext[string] | undefined {
  if (value === null) return null;

  if (typeof value === "boolean") return value;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string") {
    return sanitizeErrorText(value, MAX_CONTEXT_STRING_LENGTH);
  }

  return undefined;
}

function sanitizeEmbeddedUrl(matchedUrl: string): string {
  try {
    const parsedUrl = new URL(matchedUrl);

    return `${parsedUrl.protocol}//${parsedUrl.host}${parsedUrl.pathname}`;
  } catch {
    return "[REDACTED_URL]";
  }
}

/** Read an own property's value descriptor without invoking accessor getters. */
function readOwnValue(record: Record<string, unknown>, key: string): unknown {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(record, key);

    if (!descriptor || !("value" in descriptor)) return undefined;

    return descriptor.value;
  } catch {
    return undefined;
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const MAX_SOURCE_LENGTH = 64;
const MAX_USER_AGENT_LENGTH = 300;
const MAX_BUILD_VERSION_LENGTH = 64;

/**
 * Purpose: validate the shape/size of an incoming `POST /log/errors/action`
 * body before it is forwarded to the backend, and re-sanitize every field —
 * the action wrapper only parses JSON, it never validates or trusts it.
 * Returns `null` when the body does not look like a report at all.
 *
 * @example
 * const report = sanitizeIncomingErrorReport(body);
 * if (!report) return badRequest({ error: "Invalid error report" });
 */
export function sanitizeIncomingErrorReport(
  body: unknown,
): ErrorLogData | null {
  if (!isPlainRecord(body)) return null;

  const message = sanitizeErrorText(asNonEmptyString(body.message));

  if (!message) return null;

  const source = sanitizeErrorText(asNonEmptyString(body.source), MAX_SOURCE_LENGTH);

  if (!source) return null;

  const url = typeof body.url === "string" ? sanitizeReportUrl(body.url) : "";
  const userAgent = sanitizeErrorText(asNonEmptyString(body.userAgent), MAX_USER_AGENT_LENGTH) ?? "";
  const timestamp =
    typeof body.timestamp === "number" && Number.isFinite(body.timestamp)
      ? body.timestamp
      : Date.now();

  return {
    message,
    stack: sanitizeErrorText(asNonEmptyString(body.stack), MAX_STACK_LENGTH),
    source,
    url,
    userAgent,
    timestamp,
    buildVersion: sanitizeErrorText(asNonEmptyString(body.buildVersion), MAX_BUILD_VERSION_LENGTH),
    app: body.app === "web" ? "web" : undefined,
    additionalData: sanitizeErrorContext(
      isPlainRecord(body.additionalData) ? body.additionalData : undefined,
    ),
  };
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

export {
  MAX_MESSAGE_LENGTH,
  MAX_STACK_LENGTH,
  MAX_URL_LENGTH,
  MAX_CONTEXT_ENTRIES,
  MAX_CONTEXT_STRING_LENGTH,
  MAX_COMPONENT_STACK_LENGTH,
};
